import { useEffect, useState } from "react";

import { Loader2, TrendingUp, Clock, XCircle, CheckCircle } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";

type FlowAnalytics = {
  flow_type: string;
  total_sessions: number;
  completed_sessions: number;
  abandoned_sessions: number;
  avg_duration_seconds: number;
  median_duration_seconds: number;
};

type StepAnalytics = {
  flow_type: string;
  step_name: string;
  step_entries: number;
  avg_duration_seconds: number;
  median_duration_seconds: number;
  max_duration_seconds: number;
};

const BoardCreationAnalytics = () => {
  const [flowAnalytics, setFlowAnalytics] = useState<FlowAnalytics[]>([]);
  const [stepAnalytics, setStepAnalytics] = useState<StepAnalytics[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [flowResult, stepResult] = await Promise.all([
        supabase.from("board_creation_analytics").select("*"),
        supabase.from("board_creation_step_analytics").select("*"),
      ]);

      if (flowResult.error) throw flowResult.error;
      if (stepResult.error) throw stepResult.error;

      setFlowAnalytics(flowResult.data || []);
      setStepAnalytics(stepResult.data || []);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatFlowType = (flowType: string | null) => {
    if (!flowType) return "Unknown";
    return flowType.charAt(0).toUpperCase() + flowType.slice(1);
  };

  const calculateCompletionRate = (completed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const calculateAbandonmentRate = (abandoned: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((abandoned / total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const totalSessions = flowAnalytics.reduce((sum, f) => sum + f.total_sessions, 0);
  const totalCompleted = flowAnalytics.reduce((sum, f) => sum + f.completed_sessions, 0);
  const totalAbandoned = flowAnalytics.reduce((sum, f) => sum + f.abandoned_sessions, 0);

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSessions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCompleted}</div>
            <p className="text-xs text-muted-foreground">
              {calculateCompletionRate(totalCompleted, totalSessions)}% completion rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Abandoned</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalAbandoned}</div>
            <p className="text-xs text-muted-foreground">
              {calculateAbandonmentRate(totalAbandoned, totalSessions)}% abandonment rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatDuration(
                flowAnalytics.reduce((sum, f) => sum + (f.avg_duration_seconds || 0) * f.total_sessions, 0) / 
                totalSessions || 0
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Flow Type Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Flow Type Breakdown</CardTitle>
          <CardDescription>
            Performance metrics by board creation flow type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Flow Type</TableHead>
                <TableHead className="text-right">Sessions</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Abandoned</TableHead>
                <TableHead className="text-right">Completion Rate</TableHead>
                <TableHead className="text-right">Avg Time</TableHead>
                <TableHead className="text-right">Median Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {flowAnalytics.map((flow) => (
                <TableRow key={flow.flow_type}>
                  <TableCell className="font-medium">
                    {formatFlowType(flow.flow_type)}
                  </TableCell>
                  <TableCell className="text-right">{flow.total_sessions}</TableCell>
                  <TableCell className="text-right">{flow.completed_sessions}</TableCell>
                  <TableCell className="text-right">{flow.abandoned_sessions}</TableCell>
                  <TableCell className="text-right">
                    {calculateCompletionRate(flow.completed_sessions, flow.total_sessions)}%
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDuration(flow.avg_duration_seconds || 0)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDuration(flow.median_duration_seconds || 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Step-by-Step Analytics */}
      <Card>
        <CardHeader>
          <CardTitle>Step-by-Step Analysis</CardTitle>
          <CardDescription>
            Time spent in each step of the flow
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Flow Type</TableHead>
                <TableHead>Step</TableHead>
                <TableHead className="text-right">Entries</TableHead>
                <TableHead className="text-right">Avg Time</TableHead>
                <TableHead className="text-right">Median Time</TableHead>
                <TableHead className="text-right">Max Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stepAnalytics.map((step, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">
                    {formatFlowType(step.flow_type)}
                  </TableCell>
                  <TableCell>{step.step_name}</TableCell>
                  <TableCell className="text-right">{step.step_entries}</TableCell>
                  <TableCell className="text-right">
                    {formatDuration(step.avg_duration_seconds || 0)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDuration(step.median_duration_seconds || 0)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDuration(step.max_duration_seconds || 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default BoardCreationAnalytics;
